import { useState, type FormEvent } from "react";
import { aboutInfo, siteStats, teamMembers } from "@/mocks/mv";
import { useSeo } from "@/hooks/useSeo";

const FORM_URL = "https://readdy.ai/api/form/db96gu0kk00l6eltrphg";

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useSeo({
    title: "红桃MV视频 - 关于我们",
    description:
      "了解红桃MV视频的定位、使命与团队，我们致力于为乐迷提供专业、新鲜的音乐视频聚合与观看体验。",
    keywords: "红桃MV视频,关于我们,音乐视频平台,运营团队,联系方式",
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const honeypot = (data.get("website_alt") as string || "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    setStatus("sending");
    setFormError("");

    try {
      const body = new URLSearchParams();
      data.forEach((value, key) => {
        if (key === "website_alt") return;
        body.append(key, value as string);
      });

      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isOk = response.ok && parsed?.code === "OK" && !serverMsg.toLowerCase().includes("spam");

      if (isOk) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg && !serverMsg.toLowerCase().includes("spam") ? serverMsg : "提交失败，请稍后重试");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试");
    }
  };

  return (
    <div className="w-full">
      <div className="border-b border-background-800 bg-background-900/50 px-4 py-12 md:px-8">
        <span className="inline-flex items-center gap-2 rounded-full bg-primary-500/15 px-3 py-1 text-xs text-primary-300">
          <i className="ri-music-2-line" aria-hidden="true"></i>关于红桃MV视频
        </span>
        <h1 className="mt-4 max-w-3xl font-heading text-2xl font-bold leading-tight text-foreground-50 md:text-4xl">
          让每一支好 MV，都被认真听歌的人看见
        </h1>
        <p className="mt-4 max-w-3xl text-sm leading-7 text-foreground-300">{aboutInfo.mission}</p>
      </div>

      <div className="w-full px-4 py-10 md:px-8">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {siteStats.map((s) => (
            <div key={s.label} className="rounded-lg border border-background-800 bg-background-900 p-5 text-center">
              <p className="font-heading text-2xl font-bold text-accent-500">{s.value}</p>
              <p className="mt-1 text-xs text-foreground-400">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-50 md:text-2xl">我们做什么</h2>
            <p className="mt-4 text-sm leading-7 text-foreground-300">{aboutInfo.intro}</p>
            <div className="mt-6 space-y-3">
              {[
                { icon: "ri-fire-fill", text: "持续收录全球最新、最热门的 MV 作品" },
                { icon: "ri-list-check-2", text: "清晰的风格分类、多维筛选与权威榜单" },
                { icon: "ri-search-line", text: "支持 MV 名称与歌手的快速模糊搜索" },
                { icon: "ri-heart-3-fill", text: "为乐迷打造的干净、专注的观看体验" },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3 rounded-lg border border-background-800 bg-background-900 p-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500/15 text-primary-400">
                    <i className={item.icon} aria-hidden="true"></i>
                  </span>
                  <span className="text-sm text-foreground-300">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-50 md:text-2xl">联系方式</h2>
            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-3 rounded-lg border border-background-800 bg-background-900 p-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-500/15 text-lg text-secondary-400"><i className="ri-mail-line" aria-hidden="true"></i></span>
                <div>
                  <p className="text-xs text-foreground-500">邮箱</p>
                  <p className="text-sm text-foreground-100">{aboutInfo.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-lg border border-background-800 bg-background-900 p-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-500/15 text-lg text-secondary-400"><i className="ri-phone-line" aria-hidden="true"></i></span>
                <div>
                  <p className="text-xs text-foreground-500">商务合作</p>
                  <p className="text-sm text-foreground-100">{aboutInfo.phone}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-lg border border-background-800 bg-background-900 p-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-500/15 text-lg text-secondary-400"><i className="ri-map-pin-line" aria-hidden="true"></i></span>
                <div>
                  <p className="text-xs text-foreground-500">地址</p>
                  <p className="text-sm text-foreground-100">{aboutInfo.address}</p>
                </div>
              </div>
            </div>
            <div className="mt-4 flex gap-3">
              {[
                { icon: "ri-weibo-line", label: "微博" },
                { icon: "ri-wechat-line", label: "微信" },
                { icon: "ri-tiktok-line", label: "抖音" },
                { icon: "ri-bilibili-line", label: "B站" },
              ].map((s) => (
                <a key={s.label} href="#" aria-label={s.label} className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md bg-background-800 text-foreground-300 transition-colors hover:bg-primary-500 hover:text-background-50">
                  <i className={s.icon} aria-hidden="true"></i>
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12">
          <h2 className="font-heading text-xl font-bold text-foreground-50 md:text-2xl">运营团队</h2>
          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {teamMembers.map((m) => (
              <div key={m.name} className="overflow-hidden rounded-lg border border-background-800 bg-background-900 text-center">
                <div className="aspect-square w-full overflow-hidden">
                  <img src={m.avatar} alt={`${m.name} - ${m.role}`} title={`${m.name} - ${m.role}`} className="h-full w-full object-cover" />
                </div>
                <div className="p-3">
                  <p className="text-sm font-medium text-foreground-100">{m.name}</p>
                  <p className="mt-0.5 text-xs text-foreground-400">{m.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 rounded-lg border border-background-800 bg-background-900 p-6 md:p-8">
          <h2 className="font-heading text-xl font-bold text-foreground-50 md:text-2xl">用户反馈</h2>
          <p className="mt-2 text-sm text-foreground-400">
            有想看的 MV、功能建议或合作意向？欢迎留言告诉我们（表单为前端演示提交，我们会尽快处理）。
          </p>

          <form
            id="about-feedback-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2"
          >
            <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" readOnly aria-hidden="true" className="field-website-alt" />
            <div className="flex flex-col gap-2">
              <label htmlFor="fb-name" className="text-sm text-foreground-300">昵称</label>
              <input id="fb-name" name="name" required placeholder="怎么称呼你" className="rounded-md border border-background-700 bg-background-950 px-3 py-2.5 text-sm text-foreground-100 outline-none placeholder:text-foreground-500 focus:border-primary-500" />
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="fb-email" className="text-sm text-foreground-300">邮箱</label>
              <input id="fb-email" name="email" type="email" required placeholder="用于接收回复" className="rounded-md border border-background-700 bg-background-950 px-3 py-2.5 text-sm text-foreground-100 outline-none placeholder:text-foreground-500 focus:border-primary-500" />
            </div>
            <div className="flex flex-col gap-2 md:col-span-2">
              <label htmlFor="fb-message" className="text-sm text-foreground-300">留言内容</label>
              <textarea id="fb-message" name="message" required maxLength={500} rows={5} placeholder="写下你的建议或想看的 MV（最多 500 字）" className="resize-none rounded-md border border-background-700 bg-background-950 px-3 py-2.5 text-sm text-foreground-100 outline-none placeholder:text-foreground-500 focus:border-primary-500"></textarea>
            </div>
            <div className="flex flex-wrap items-center gap-4 md:col-span-2">
              <button type="submit" disabled={status === "sending"} className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60">
                {status === "sending" ? "提交中…" : "提交反馈"}
              </button>
              {status === "success" && (
                <span className="flex items-center gap-1 text-sm text-accent-500">
                  <i className="ri-checkbox-circle-line" aria-hidden="true"></i>提交成功，感谢你的反馈！
                </span>
              )}
              {status === "error" && (
                <span className="flex items-center gap-1 text-sm text-secondary-400">
                  <i className="ri-error-warning-line" aria-hidden="true"></i>{formError}
                </span>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}